import { RefObject, useCallback, useMemo, useRef, useState } from 'react';

/** Rows shown per page in the calendar "List" views. Kept small so the DOM stays light. */
export const LIST_VIEW_PAGE_SIZE = 20;

export type PagedList<T> = {
    /** Zero-based current page, clamped so it is always valid for the current item count */
    page: number;
    pageCount: number;
    pageSize: number;
    /** Index of the first item on this page within `items` */
    offset: number;
    total: number;
    pageItems: T[];
    /** Changes page and scrolls `topRef` into view when attached */
    goToPage: (page: number) => void;
    /** Attach to the element that should scroll into view when the page changes */
    topRef: RefObject<HTMLDivElement>;
};

/**
 * Client-side pagination over an in-memory array. The page clamps rather than resets when
 * the array shrinks (e.g. after a delete), so the user stays where they were when possible.
 */
export function usePagedList<T>(items: T[], pageSize: number = LIST_VIEW_PAGE_SIZE): PagedList<T> {
    const [requestedPage, setRequestedPage] = useState(0);
    const topRef = useRef<HTMLDivElement>(null);

    const total = items.length;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(Math.max(requestedPage, 0), pageCount - 1);
    const offset = page * pageSize;

    const pageItems = useMemo(() => items.slice(offset, offset + pageSize), [items, offset, pageSize]);

    const goToPage = useCallback((nextPage: number) => {
        setRequestedPage(nextPage);

        const el = topRef.current;
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    return { page, pageCount, pageSize, offset, total, pageItems, goToPage, topRef };
}
